import React, {useCallback, useContext, useState} from 'react';
import {HTMLDialog, Icon, PriorityIcon} from '@priority-software/priority-style-react';
import cx from 'classnames';
import {DialogToolbarContent} from '../../../../components/Dialog/DialogToolbar/DialogToolbar';
import {RuleContent} from './parts/AutomationRuleContent/RuleContent';
import {AutomationRuleFooter} from './parts/AutomationRuleFooter/index';
import {AutomationContext} from '../../context';
import {RuleType} from '../../enums';
import {ruleDialogID} from '../../constants';
import {RuleDialogContext} from '../RulesListDialog/context';
import {BusinessRuleHelp} from '../RulesListDialog/consts';
import {texts} from '../../texts';
import {useRuleContent} from './parts/AutomationRuleContent/hooks/useRuleContent';
import {ModernTooltip} from '../../../../components/Common/Tooltip/ModernTooltip';
import {isRTL} from '../../../../Localization';
import dialogCSS from '../../common-dialog-styles.module.scss';
import {isSaveEnabled} from './parts/AutomationRuleFooter/utils';
import {Loader} from '../../../OnboardingDialog/components';
import styles from './styles.module.scss';


type Props = {
    type: RuleType;
    isOpen: boolean;
    showValidForOptions?: boolean;
    title: string;
    subtitle?: string | React.ReactNode;
    specialCondition?: React.ReactElement;
    help?: string;
    session: string;
    currentFormTitle?: string;
};

export const RuleDialogComponent = (props: Props) => {
    const {
        isOpen,
        type,
        showValidForOptions,
        title,
        subtitle,
        specialCondition,
        help,
        session,
        currentFormTitle = ''
    } = props;
    const {
        closeSingleRuleDialog,
        openRule,
        saveRule,
        updateOpenRule,
        closeCurrentAutomation,
        aiGeneratedRuleQuery,
        newEditorSupport
    } = useContext(AutomationContext);

    const [escClosable, setClosable] = useState(true);
    const [aiLoading, setAiLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const {state, handlers} = useRuleContent({
        session,
        rule: openRule,
        type,
        saveRule,
        onRuleUpdate: updateOpenRule,
        closeSingleRuleDialog,
        closeCurrentAutomation,
        isAIRule: !!aiGeneratedRuleQuery,
        newEditorSupport
    });

    const {conditionOperator, isRuleActive, conditions, hasTemporaryEmailTemplate} = state;

    const handleEscClosable = useCallback((flag: boolean) => {
        setClosable(flag);
    }, []);

    // Create a custom onRuleSave handler that will show/hide the loader
    const handleRuleSave = useCallback(async () => {
        try {
            setIsSaving(true);
            await handlers.onRuleSave();
        } finally {
            setIsSaving(false);
        }
    }, [handlers.onRuleSave]);

    const monitorTransactionText = type === RuleType.business ? BusinessRuleHelp : '';
    const isValidRule = isSaveEnabled(openRule, type);

    return (
        <RuleDialogContext.Provider
            value={{
                escClosable,
                handleEscClosable,
                session,
                closeCurrentAutomation,
                hasTemporaryEmailTemplate,
                aiLoading,
                setAiLoading,
                toggleHasTemporaryEmailTemplate: handlers.toggleHasTemporaryEmailTemplate
            }}
        >
            <HTMLDialog
                escClosable={escClosable}
                id={ruleDialogID}
                isOpen={isOpen}
                onClose={handlers.onBack}
                closable={false}
                componentClasses={cx(styles.component, dialogCSS.component, 'reactComp')}
                toolbarContent={
                    <>
                        <ModernTooltip
                            tooltip={texts.go_back_btn_tooltip}
                            delay={300}
                            position="top-start"
                            portalId={ruleDialogID}
                        >
                            <Icon
                                hoverable
                                icon={isRTL() ? PriorityIcon.arrowRight : PriorityIcon.arrowLeft}
                                onClick={handlers.onBack}
                                componentClasses={styles.backBtn}
                            />
                        </ModernTooltip>

                        <DialogToolbarContent
                            showSeparator={false}
                            mainTitle={title}
                            subTitle={subtitle}
                            helpText={help}
                            tooltipPortalID={ruleDialogID}
                            monitorTransactionText={monitorTransactionText}
                        />
                    </>
                }
                content={
                    <>
                        {isSaving && (
                            <div className={styles.loaderOverlay}>
                                <Loader />
                            </div>
                        )}
                        <RuleContent
                            session={session}
                            rule={openRule}
                            ruleType={type}
                            conditionOperator={conditionOperator}
                            conditions={conditions}
                            isRuleActive={isRuleActive}
                            onRuleUpdate={updateOpenRule}
                            specialCondition={specialCondition}
                            {...handlers}
                        />
                    </>
                }
                footer={
                    <AutomationRuleFooter
                        type={type}
                        showValidForOptions={showValidForOptions}
                        formName={currentFormTitle}
                        session={session}
                        onRuleUpdate={updateOpenRule}
                        onClose={handlers.onBack}
                        onRuleSave={handleRuleSave}
                        handleRuleFormData={handlers.handleRuleFormData}
                        isValidRule={isValidRule}
                    />
                }
                size="large"
            />
        </RuleDialogContext.Provider>
    );
};
